import { and, eq, not } from 'tio-ui/utilities';
import * as v from 'valibot';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import { dropTask } from 'ember-concurrency';
import { fn, concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
} from '../../../utils/tuition-assistance/fields.ts';
import AlertBanner from 'tio-ui/components/alert-banner';
import ApprovalRequestSummaryAndApprovedTotal from './courses-status-approval/request-summary-and-approved-total.gts';
import ClaimsFinancialsCalculationsReadonlyCard from '../program-details/claims-financials/calculations-readonly-card.gts';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import errorsForField, { type ValidationError } from '../../../helpers/errors-for-field.ts';
import RadioGroup from 'tio-ui/components/radio-group';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import LimitsSummary from '../limits-summary.gts';
import type RouterService from '@ember/routing/router-service';
import safeFormatNumber from '../../../helpers/safe-format-number';
import type Store from '@ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import ErrorMessages from 'tio-ui/components/error-messages';
import TuitionAssistanceApplicationDetailsClaimsFinancials from './courses-status-approval/claims-financials.gts';
import TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist from './courses-approval-waiver-checklist.gts';
import type {
  TasApplicationState,
  TasClaimsFinancials,
} from '../../../types/tuition-assistance.ts';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import SelectTextArea from '../../tio/select-text-area.gts';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import RequestSummary from '../../tas/approval/request-summary.gts';

import type SessionContextService from '../../../services/session-context.ts';
type ActionSubMap = {
  true: string;
  CHANGE_REQUESTED: string;
};

type ActionMapType = {
  [key in TasApplicationState]?: ActionSubMap;
};

interface TuitionAssistanceApplicationDetailsCoursesStatusApprovalSignature {
  Args: {
    application: TasApplicationModel;
    programInstance: TasProgramInstanceModel;
    routePrefix: string;
    isReadonlyView: boolean; // This is poorly named should be like isNonApprovalView
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceApplicationDetailsCoursesStatusApprovalComponent extends Component<TuitionAssistanceApplicationDetailsCoursesStatusApprovalSignature> {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare sessionContext: SessionContextService;
  @service employee: unknown;

  @tracked customNote = '';
  @tracked rejectNote = '';
  @tracked approveStatusResponse: boolean | 'CHANGE_REQUESTED' | null = null;
  @tracked showRejectModal = false;
  @tracked hasSubmitted = false;
  @tracked rejectError = null;
  @tracked submitError: string | null = null;
  @tracked showConfirmApproverOverridePrompt = false;
  @tracked showConfirmSubmitPrompt = false;
  @tracked editableApplicationTotal = 0;
  @tracked editableCourseCashTotal = 0;
  @tracked hasConfirmedEvidenceAttachmentWaiver = false;
  @tracked hasConfirmedEvidenceApprovalWaiver = false;
  @tracked hasRespondedToAllApprovalWaiverChecklistItems = false;
  @tracked requestedRepaymentAmount = 0;
  @tracked reasonForRepaymentRequest: string = 'Pre-paid application rejected by approver';
  @tracked showIgnorePoliciesModal = false;
  @tracked isRejectionTaskRunning = false;
  @tracked submitOnBehalfMissingRecs?: string[] = [];

  getSingleValueForTasField = getSingleValueForTasField;

  constructor(
    owner: Owner,
    args: TuitionAssistanceApplicationDetailsCoursesStatusApprovalSignature['Args']
  ) {
    super(owner, args);
    this.editableApplicationTotal = this.calculateTotalByClaimsFinancials
      ? args.application.recalculateClaimsFinancialsAfterAdjacentUpdate().CF_REIMBURSEMENT_TOTAL
      : this.readonlyOriginalApplicationTotal;
    this.requestedRepaymentAmount = args.application.canRequestRepayment
      ? args.application.paidTotal
      : 0;
    this.editableCourseCashTotal = Number(args.application.cardPreApprovedTotal);
  }

  toDollars(cents: number) {
    if (!cents) return;
    return cents / 100;
  }

  get isCreditBased() {
    return this.args.application.isCreditBased;
  }

  /**
   * The raw approved total in cents from the backend field.
   */
  get rawApprovedTotalInCents() {
    return (
      (this.getSingleValueForTasField(
        this.fieldNameToEdit,
        this.args.application.fields
      ) as number) ??
      (this.args.application.approvedTotal as number) ??
      (this.args.application.requestedTotal as number)
    );
  }

  /**
   * For credit-based programs, converts cents → credits for display.
   * For monetary programs, returns value in cents as-is.
   */
  get readonlyOriginalApplicationTotal() {
    const rawCents = this.rawApprovedTotalInCents;
    return rawCents;
  }

  get readonlyCourseCashTuitionTotal() {
    return (
      (this.args.application.cardFinalApprovedTotal as number) ??
      (this.args.application.cardPreApprovedTotal as number) ??
      0
    );
  }

  get fieldNameToEdit() {
    return this.args.application.fieldForEditableApprovedTotal;
  }

  // This excludes calculateTotalByCredit on purpose
  get isCalculatedApplicationTotal() {
    return this.calculateTotalByPercent || this.calculateTotalByGPA;
  }

  get isProhibitedEdit() {
    return (
      !this.isPendingApprovalAndNotReadonly && !this.isAllowedEditApprovedTotalIfNotPendingApproval
    );
  }

  get isPendingApprovalAndNotReadonly() {
    if (this.args.isReadonlyView) {
      return false;
    }
    return !!this.args.application.state.includes('PENDING');
  }

  get coursesInAttendAndNotReadonly() {
    if (this.args.isReadonlyView) {
      return false;
    }
    const currentUser = this.sessionContext.user;

    return (
      this.args.application.state === 'TAS.ApplicationState.ATTEND' &&
      (this.isValidPossibleApproverForUpdateApprovedTotal ||
        currentUser?.isEmployeeAdmin ||
        currentUser?.isAccountOwner ||
        currentUser?.isTioAdmin ||
        currentUser?.isTasTioApprover)
    );
  }

  get hasApprovalPermissions(): boolean {
    const user = this.sessionContext.user;
    // TODO: The logic for determining employee admin or account owner roles
    // is slightly flawed and needs to be revisited.
    // Once we merge the session context service into tio-common, we can write a method to
    // determine if the user is an employeeAdmin/accountOwner of the company being viewed
    // rather than having any account owner role. (This is only an issue for tio-admin app)
    return Boolean(
      this.isValidPossibleApproverForUpdateApprovedTotal ||
      user?.isEmployeeAdmin ||
      user?.isAccountOwner ||
      user?.isTioAdmin ||
      user?.isTasTioApprover ||
      (this.args.application.isPrepaidProgram && user?.isPayOps)
    );
  }

  get isAllowedEditApprovedTotalIfNotPendingApproval() {
    if (!this.args.application.isEditableApprovedTotal) {
      return false;
    }
    return this.hasApprovalPermissions;
  }

  get adapterMethod() {
    const actionMap: ActionMapType = {
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL': {
        true: 'approveCourses',
        CHANGE_REQUESTED: 'requestChanges',
      },
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': {
        true: 'approveCourseEvidence',
        CHANGE_REQUESTED: 'requestCourseEvidenceChanges',
      },
    };

    const adapterActions = actionMap[this.args.application.state];
    // @ts-expect-error will need help with this one
    return adapterActions?.[this.approveStatusResponse] ?? undefined;
  }

  get rejectionCall() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
      ? 'rejectCourseEvidence'
      : 'rejectCourses';
  }

  get cannedApproverResponse() {
    const templateFields = this.args.programInstance.tasProgramTemplate.fields;

    const actionMap: ActionMapType = {
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL': {
        true: getSingleValueForTasField('COURSES_APPROVAL_APPROVE_TEXT', templateFields),
        CHANGE_REQUESTED: getSingleValueForTasField('COURSES_APPROVAL_REVISE_TEXT', templateFields),
      },
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': {
        true: getSingleValueForTasField('EVIDENCE_APPROVAL_APPROVE_TEXT', templateFields),
        CHANGE_REQUESTED: getSingleValueForTasField(
          'EVIDENCE_APPROVAL_REVISE_TEXT',
          templateFields
        ),
      },
    };

    const response = actionMap[this.args.application.state];
    // @ts-expect-error will need help with this one
    return response?.[this.approveStatusResponse] ?? '';
  }

  get isPendingCoursesApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';
  }

  get canSkipFinalApprovalOrAttachmentsWithChecklist() {
    return (
      this.isPendingApprovalAndNotReadonly &&
      this.isPendingCoursesApproval &&
      getSingleValueForTasField(
        'ALLOW_WAIVER_OF_EVIDENCE_APPROVAL',
        this.args.application.tasProgramInstance.tasProgramTemplate.fields
      ) === true
    );
  }

  get isApproveAction() {
    return this.approveStatusResponse === true;
  }

  get isFormValid() {
    if (this.canSkipFinalApprovalOrAttachmentsWithChecklist && this.isApproveAction) {
      return (
        this.hasRespondedToAllApprovalWaiverChecklistItems && !this.formValidationErrors.length
      );
    }
    return !this.formValidationErrors.length;
  }

  get formValidationErrors() {
    const invoiceDateSchema = this.invoiceDateConfig?.required
      ? v.pipe(
          v.string('Invoice date is required'),
          v.nonEmpty('Invoice date is required'),
          v.isoDate('Invalid date')
        )
      : v.optional(v.nullable(v.pipe(v.string(), v.isoDate('Invalid date'))));

    const schema = v.object({
      customNote: v.pipe(v.string('Please provide a note'), v.nonEmpty('Please provide a note')),
      invoiceDate: invoiceDateSchema,
      approvalAction: v.pipe(
        v.nullable(v.union([v.literal(true), v.literal(false), v.literal('CHANGE_REQUESTED')])),
        v.check((value) => value !== null, 'Please select an action')
      ),
    });

    const formModel = {
      customNote: this.customNote,
      invoiceDate: getSingleValueForTasField('INVOICE_DATE', this.args.application.fields),
      approvalAction: this.approveStatusResponse,
    };

    const result = v.safeParse(schema, formModel);

    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};

      // Convert Valibot errors to format compatible with errorsForField helper (Yup format)
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: formModel[field as keyof typeof formModel] as string,
      })) as ValidationError[];
    }

    return [] as ValidationError[];
  }

  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.application.currentExpectedApproverEmployeeIds;
    const employees = this.sessionContext.user?.person?.employees || [];

    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  get isValidPossibleApproverForUpdateApprovedTotal() {
    const allPossibleApproverEmployeeIds =
      this.args.application.allPossibleApproverEmployeeIdsForAllWorkflows;
    const employees = this.sessionContext.user?.person?.employees || [];

    return doEmployeesExistInApproverList(employees, allPossibleApproverEmployeeIds);
  }

  get employeeHasAppsInRepayment() {
    return !!this.appsInRepayment.length;
  }

  get appsInRepayment() {
    // TODO: this will need to be adjusted to send back a total amount requested as well as id
    return this.tasApplications.reduce((acc: string[], app: TasApplicationModel) => {
      if (app.isRefundRequested) {
        // @ts-expect-error TODO: figure out we type id for consistency
        acc.push(app.id);
      }
      return acc;
    }, []);
  }

  tasApplicationsData = trackedFunction(this, async () => {
    if (!this.args.programInstance.employee?.id || this.args.isReadonlyView) {
      return [];
    }

    return (await this.store.query('tas-application', {
      filter: { employee: this.args.programInstance?.employee?.id },
    })) as TasApplicationModel[];
  });

  get tasApplications() {
    return this.tasApplicationsData.value || [];
  }

  get automatedApprovalNoteIfApplicable() {
    if (this.calculateTotalByCredit) {
      return `${this.args.application.requestedTotalCalculationFormulaNote} (Pre-calculated requested total before scholarships or approver edits)`;
    }
    if (this.calculateTotalByPercent) {
      return `${this.args.application.requestedTotalCalculationFormulaNote}`;
    }
    return '';
  }

  get calculateTotalByCredit() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByCredit;
  }

  get calculateTotalByPercent() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByPercent;
  }

  get calculateTotalByClaimsFinancials() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByClaimsFinancials;
  }

  get calculateTotalByGPA() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByGPA;
  }

  get approvedTotalUpdateReasoning() {
    // Include their comments with a system message indicating
    //  the approval action (approve/reject/etc) that triggered the update.

    return this.automatedApprovalNoteIfApplicable
      ? `${this.customNote} [system_message: Updated via '${this.adapterMethod}'. Initial calculation notes: ${this.automatedApprovalNoteIfApplicable}]`
      : `${this.customNote} [system_message: Updated via '${this.adapterMethod}']`;
  }

  get tasParticipant() {
    return this.args.programInstance?.employee?.tasParticipant;
  }

  get isTasParticipantInactive() {
    return !this.tasParticipant.isActive;
  }

  get participantId() {
    return this.tasParticipant.id;
  }

  get invoiceDateConfig() {
    return this.args.programInstance.tasProgramTemplate.fields['INVOICE_DATE'];
  }

  updateInvoiceDate = async (date: string) => {
    setSingleValueForTasField('INVOICE_DATE', date, this.args.application.fields);
    await this.args.application.save();
  };

  get showNewRequestSummary() {
    return this.args.application.shouldDisplayPaymentDetails;
  }

  submitTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;

    if (this.formValidationErrors.length) {
      return;
    }

    this.hasSubmitted = true;
    this.submitError = null;

    try {
      await this.handleApprovedTotalUpdate();

      if (this.args.programInstance.tasProgramTemplate.isVoucherProgram) {
        setSingleValueForTasField(
          'VOUCHER_AMOUNT',
          this.tuitionAssistance.availableVoucherAmount,
          this.args.application.fields
        );
      }
      await this.args.application.save();

      if (this.isApproveAction && this.hasConfirmedEvidenceAttachmentWaiver) {
        await this.waiveRequirementsIfApplicable(this.args.application);
      }

      await this.submitApplication();

      if (this.isApproveAction && this.hasConfirmedEvidenceApprovalWaiver) {
        await this.triggerAutoEvidenceApproval();
      }

      // Redirect to the new TAS approval queue using the appropriate route prefix
      this.router.transitionTo(`${this.args.routePrefix}.tas.approval-queue`);
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  get paymentMethodIsCourseCash() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  setCourseCashApprovedTotal = async (amount: number) => {
    const isCourseCash = this.paymentMethodIsCourseCash;
    if (!isCourseCash && !amount) return;

    setSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', amount, this.args.application.fields);
    await this.args.application.save();
  };

  // Submit task helper functions
  async handleApprovedTotalUpdate() {
    let additionalFieldValues: Record<string, number> | undefined;

    if (this.paymentMethodIsCourseCash) {
      additionalFieldValues = { CARD_FINAL_APPROVED_TOTAL: this.editableCourseCashTotal };
    }

    return this.updateApprovedTotal(
      this.editableApplicationTotal,
      this.approvedTotalUpdateReasoning,
      additionalFieldValues
    );
  }

  async submitApplication() {
    return this.store
      .adapterFor('tas-application')
      [this.adapterMethod](this.args.application, this.customNote);
  }

  async triggerAutoEvidenceApproval() {
    await this.tuitionAssistance.triggerAutoEvidenceApprovalForApplication(
      this.args.application,
      this.customNote
    );
  }
  // End submit task helper functions

  get missingRequirementsForSubmitOnEmployeeBehalf() {
    const {
      tasProgramInstance,
      isMissingCourseGrades,
      isMissingRequiredEvidence,
      requiresGPA,
      gradePointAverage,
    } = this.args.application;
    let missingRecs: string[] = [];
    const courseGradeRequired = tasProgramInstance.tasProgramTemplate.courseGradeRequired;
    if (courseGradeRequired && isMissingCourseGrades) {
      missingRecs = [...missingRecs, ' course grade(s)'];
    }

    if (isMissingRequiredEvidence) {
      missingRecs = [...missingRecs, ' evidence attachment'];
    }

    if (requiresGPA && !gradePointAverage) {
      missingRecs = [...missingRecs, ' GPA'];
    }
    return missingRecs;
  }

  get missingRecsAlert() {
    return `You are missing the following requirement(s):${this.missingRequirementsForSubmitOnEmployeeBehalf}`;
  }

  submitForEvidenceApprovalOnEmployeeBehalf = dropTask(async () => {
    this.submitOnBehalfMissingRecs = this.missingRequirementsForSubmitOnEmployeeBehalf;
    this.hasSubmitted = true;
    if (this.submitOnBehalfMissingRecs.length === 0) {
      await this.store.adapterFor('tas-application').requestCourseEvidence(this.args.application);
      await this.args.application.reload();
    }
  });

  waiveRequirementsIfApplicable = async (application: TasApplicationModel) => {
    if (
      !this.canSkipFinalApprovalOrAttachmentsWithChecklist ||
      !this.isApproveAction ||
      // Evidence approval can only be waived if attachment requirement is waived also
      !this.hasConfirmedEvidenceAttachmentWaiver
    ) {
      return;
    }

    if (this.hasConfirmedEvidenceApprovalWaiver) {
      // This endpoint sets the fields WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT and WAIVE_EVIDENCE_APPROVAL_REQUIREMENT to true
      // and overrides the approval summaries to enable skipping subsequent approval steps
      return this.store.adapterFor('tas-application').waiveEvidenceApproval(application);
    } else {
      // If we are not waiving full approval, we still waive the attachment requirement by setting this field
      setSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', true, application.fields);
      return application.save();
    }
  };

  get confirmSubmitMessage() {
    if (this.approveStatusResponse === true)
      return this.intl.t('common.tuition_assistance.approval.confirm_approve');
    if (this.approveStatusResponse === false)
      return this.intl.t('common.tuition_assistance.approval.confirm_reject');
    return this.intl.t('common.tuition_assistance.approval.confirm_request_changes');
  }

  attemptSubmitApproval = () => {
    this.hasSubmitted = true;

    if (this.formValidationErrors.length) {
      return;
    }

    this.showConfirmSubmitPrompt = true;
  };

  confirmSubmitApproval = () => {
    this.showConfirmSubmitPrompt = false;

    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitTask.perform();
    }
  };

  cancelSubmitApproval = () => {
    this.showConfirmSubmitPrompt = false;
  };

  setApplicationTotal = (value: number) => {
    this.editableApplicationTotal = Math.round(value * 100);
  };

  setCourseCashTotal = (value: number) => {
    this.editableCourseCashTotal = Math.round(value * 100);
  };

  updateApprovedTotal = async (
    approvedTotal: number,
    reason: string,
    additionalFieldsToUpdate?: Record<string, unknown>
  ) => {
    const additionalFieldValues: {
      CARD_FINAL_APPROVED_TOTAL?: number;
      CLAIMS_FINANCIALS?: TasClaimsFinancials;
    } = {
      ...additionalFieldsToUpdate,
    };

    if (this.calculateTotalByClaimsFinancials) {
      const claims = this.args.application.recalculateClaimsFinancialsAfterAdjacentUpdate();
      additionalFieldValues['CLAIMS_FINANCIALS'] = claims;
    }

    const additionalKeyAdded = Object.keys(additionalFieldValues).length > 0;

    // Don't save the course cash total separately - let updateApprovedTotal handle it
    // This ensures the approved_total_log captures the correct "from" value

    if (
      this.calculateTotalByClaimsFinancials ||
      !this.showNewRequestSummary ||
      additionalKeyAdded
    ) {
      const updatedApplication = await this.store
        .adapterFor('tas-application')
        .updateApprovedTotal(this.args.application, approvedTotal, reason, additionalFieldValues);

      // Instead of comparing against updatedApplication.approvedTotal directly,
      // we need to check the actual field that was updated based on the application state
      // Based on backend logic: BEFORE_PRE_APPROVAL_STATE_STRINGS includes states before ATTEND
      const beforePreApprovalStates = [
        'TAS.ApplicationState.CREATED',
        'TAS.ApplicationState.DEFINE_COURSES',
        'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
        'TAS.ApplicationState.COURSES_APPROVED',
        'TAS.ApplicationState.COURSES_NOT_APPROVED',
        'TAS.ApplicationState.COURSES_REJECTED',
      ];

      const isBeforePreApprovalState = beforePreApprovalStates.includes(
        this.args.application.state
      );
      const fieldToCheck = isBeforePreApprovalState ? 'PRE_APPROVED_TOTAL' : 'FINAL_APPROVED_TOTAL';
      const actualUpdatedValue = getSingleValueForTasField(
        fieldToCheck,
        updatedApplication.fields
      ) as number;

      if (actualUpdatedValue !== approvedTotal) {
        throw new Error(
          `Unable to update approved total. expected=${approvedTotal} actual=${actualUpdatedValue} (checked field: ${fieldToCheck})`
        );
      }

      this.editableApplicationTotal = updatedApplication.approvedTotal as number;
      this.editableCourseCashTotal = updatedApplication.courseCashTotal as number;
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
    }
  };

  didUpdateClaimsFinancials = (claimsFinancials: TasClaimsFinancials) => {
    setSingleValueForTasField('CLAIMS_FINANCIALS', claimsFinancials, this.args.application.fields);
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
  };

  saveUpdatedClaimsFinancialsWithApprovedTotal = async (
    claimsFinancials: TasClaimsFinancials,
    reason: string
  ) => {
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
    this.didUpdateClaimsFinancials(claimsFinancials);
    await this.updateApprovedTotal(this.editableApplicationTotal, reason);
  };

  didChangeApprovalWaiverChecklist = ({
    hasConfirmedEvidenceApprovalWaiver,
    hasConfirmedEvidenceAttachmentWaiver,
    hasRespondedToAllChecklistItems,
  }: {
    hasConfirmedEvidenceApprovalWaiver: boolean;
    hasConfirmedEvidenceAttachmentWaiver: boolean;
    hasRespondedToAllChecklistItems: boolean;
  }) => {
    this.hasConfirmedEvidenceApprovalWaiver = hasConfirmedEvidenceApprovalWaiver;
    this.hasConfirmedEvidenceAttachmentWaiver = hasConfirmedEvidenceAttachmentWaiver;
    this.hasRespondedToAllApprovalWaiverChecklistItems = hasRespondedToAllChecklistItems;
  };

  setApproveStatusResponse = (value: boolean | 'CHANGE_REQUESTED' | null) => {
    this.approveStatusResponse = value;
    if (this.cannedApproverResponse) {
      this.customNote = this.cannedApproverResponse;
    }
  };

  get radioGroupValue() {
    if (this.approveStatusResponse === true) {
      return 'true';
    } else if (this.approveStatusResponse === 'CHANGE_REQUESTED') {
      return 'CHANGE_REQUESTED';
    }
    return undefined; // RadioGroup expects undefined for no selection
  }

  handleRadioGroupChange = (value: string | number | boolean) => {
    // Cast the value to the expected type for our approval status
    if (value === true || value === 'true') {
      this.setApproveStatusResponse(true);
    } else if (value === 'CHANGE_REQUESTED') {
      this.setApproveStatusResponse('CHANGE_REQUESTED');
    } else {
      this.setApproveStatusResponse(null);
    }
  };

  confirmRejection = async () => {
    this.isRejectionTaskRunning = true;
    try {
      await this.store
        .adapterFor('tas-application')
        [this.rejectionCall](this.args.application, this.rejectNote);

      if (this.args.application.canRequestRepayment) {
        await this.store.adapterFor('tas-participant').holdTasParticipant(this.participantId);
      }

      this.showRejectModal = false;
    } catch (e) {
      this.rejectError = e;
    }
    this.isRejectionTaskRunning = false;
  };

  setShowRejectModal = () => {
    this.showRejectModal = !this.showRejectModal;
  };

  get tasResponseNotes() {
    // TODO: decide if these should be filtered by response type - James 2024-06-21
    const partnerComments = this.args.application.tasProgramInstance.employee.company.comments;
    return partnerComments
      .filter((comment) => comment.isTasApproval)
      .map((comment) => comment.tasApprovalNote);
  }

  updateRejectNote = (value: string) => {
    this.rejectNote = value;
  };

  updateCustomNote = (value: string) => {
    this.customNote = value;
  };

  <template>
    <LimitsSummary
      @application={{@application}}
      @employee={{@programInstance.employee}}
      @approvedTotalToValidate={{this.editableApplicationTotal}}
      @approvedCourseCashTotalToValidate={{this.editableCourseCashTotal}}
      @labelClass="col-span-5"
      @valueClass="col-span-2 app-status-text-brand"
      as |Summary|
    >
      <VStack>
        {{#if this.calculateTotalByClaimsFinancials}}
          {{#if @isReadonlyView}}
            <ClaimsFinancialsCalculationsReadonlyCard
              @claimsFinancialsFields={{@application.claimsFinancialsCalculatorInput}}
              @showDetails={{true}}
              class="my-4"
            />
          {{else}}
            <TuitionAssistanceApplicationDetailsClaimsFinancials
              @claimsFinancials={{@application.claimsFinancialsCalculatorInput}}
              @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
              @didSave={{this.saveUpdatedClaimsFinancialsWithApprovedTotal}}
              @didUpdate={{this.didUpdateClaimsFinancials}}
              @applicationState={{@application.state}}
              class="my-4"
            />
          {{/if}}
        {{else}}

          {{#if this.showNewRequestSummary}}
            <RequestSummary
              @application={{@application}}
              @isProhibitedEdit={{and this.isProhibitedEdit (not this.hasApprovalPermissions)}}
            />
          {{else}}
            <ApprovalRequestSummaryAndApprovedTotal
              @application={{@application}}
              @programInstance={{@programInstance}}
              @isCalculatedApplicationTotal={{this.isCalculatedApplicationTotal}}
              @readonlyOriginalApplicationTotal={{this.readonlyOriginalApplicationTotal}}
              @readonlyCourseCashTuitionTotal={{this.readonlyCourseCashTuitionTotal}}
              @updateAndSaveApprovedTotalWithComment={{this.updateApprovedTotal}}
              @setApplicationTotal={{this.setApplicationTotal}}
              @setCourseCashTotal={{this.setCourseCashTotal}}
              @isProhibitedEdit={{this.isProhibitedEdit}}
              @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
              @isAllowedEditApprovedTotalIfNotPendingApproval={{this.isAllowedEditApprovedTotalIfNotPendingApproval}}
              @isSubmitting={{this.submitTask.isRunning}}
            >
              <:warning>
                <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex" />
              </:warning>
            </ApprovalRequestSummaryAndApprovedTotal>
          {{/if}}
        {{/if}}

        {{#if this.canSkipFinalApprovalOrAttachmentsWithChecklist}}
          <TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist
            @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
            @didChange={{this.didChangeApprovalWaiverChecklist}}
            class="my-8"
          />
        {{/if}}

        {{#if this.invoiceDateConfig.visible}}
          <Section>
            <:header>
              {{t "common.tuition_assistance.approver_invoice"}}
            </:header>
            <:body>
              <dl class="flex w-full gap-4 grid grid-cols-2">
                <dt class="font-semibold">
                  {{this.invoiceDateConfig.label}}
                </dt>
                <dd>
                  <Input
                    @value={{getSingleValueForTasField "INVOICE_DATE" @application.fields}}
                    @type="date"
                    @onChange={{this.updateInvoiceDate}}
                  />
                </dd>
              </dl>
            </:body>
          </Section>
        {{/if}}

        {{#if this.isPendingApprovalAndNotReadonly}}
          <Section>
            <:header>
              {{t "common.approval"}}
              <span class="text-error-400">*</span>
            </:header>
            <:actions>
              <Button
                @intent="danger"
                data-test-open-reject-modal
                {{on "click" this.setShowRejectModal}}
              >
                {{t "tuition_assistance.application_details.reject_application"}}
              </Button>
            </:actions>
            <:body>
              <VStack>
                <HStack @collapsed={{true}}>
                  <Section class="tio-flex-between w-full">
                    <RadioGroup
                      @value={{this.radioGroupValue}}
                      @onChange={{this.handleRadioGroupChange}}
                      class="flex gap-8"
                      @errors={{if
                        this.hasSubmitted
                        (errorsForField "approvalAction" schemaErrors=this.formValidationErrors)
                      }}
                      as |Radio|
                    >
                      <Radio @value="true" @label="Approve" />
                      <Radio @value="CHANGE_REQUESTED" @label="Request Change" />
                    </RadioGroup>
                  </Section>
                </HStack>

                {{#if this.tasResponseNotes.length}}
                  <SelectTextArea
                    @options={{this.tasResponseNotes}}
                    @optionLabel="title"
                    @optionValue="body"
                    @value={{this.customNote}}
                    @onChange={{this.updateCustomNote}}
                    @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
                  />
                {{else}}
                  <Textarea
                    @value={{this.customNote}}
                    @onChange={{this.updateCustomNote}}
                    placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
                    @errors={{if
                      this.hasSubmitted
                      (errorsForField "customNote" schemaErrors=this.formValidationErrors)
                    }}
                  />
                {{/if}}

                {{#if (and this.employeeHasAppsInRepayment (eq this.approveStatusResponse true))}}
                  <ErrorMessages
                    @showErrors={{true}}
                    @error={{t "tuition_assistance.application_details.please_keep_in_mind"}}
                    class="text-sm"
                  />

                  <div class="text-sm flex gap-4 pl-16 my-4">
                    <p class="font-medium shrink-0">
                      {{t "tuition_assistance.application_details.application_id"}}
                    </p>
                    <div class="flex flex-wrap gap-4">
                      {{#each this.appsInRepayment as |id|}}
                        <LinkTo
                          class="underline tio-fg-info"
                          @route={{concat
                            @routePrefix
                            ".tuition-assistance.applications.show.index"
                          }}
                          @model={{id}}
                        >
                          {{id}}
                        </LinkTo>
                      {{/each}}
                    </div>
                  </div>
                {{/if}}

                {{#if this.isTasParticipantInactive}}
                  <ErrorMessages
                    @showErrors={{true}}
                    @error={{t "tuition_assistance.application_details.employee_inactive"}}
                    class="text-sm"
                  />
                {{/if}}

                <Button
                  @intent="primary"
                  {{on "click" this.attemptSubmitApproval}}
                  @isRunning={{this.submitTask.isRunning}}
                  disabled={{not this.isFormValid}}
                  class="self-center w-48"
                >
                  {{t "common.submit"}}
                </Button>

                {{#if this.submitError}}
                  <ErrorMessages
                    @showErrors={{true}}
                    @error={{this.submitError}}
                    class="tio-row-tight text-sm"
                  />
                {{/if}}
              </VStack>
            </:body>
          </Section>

          <ConfirmationDialog
            @isOpen={{this.showConfirmSubmitPrompt}}
            @title="Confirm Submission"
            @intent="warning"
            @onConfirm={{this.confirmSubmitApproval}}
            @onCancel={{this.cancelSubmitApproval}}
            @confirmText="Yes, Submit"
            @cancelText="Cancel"
          >
            <p class="mb-2 font-semibold">
              {{this.confirmSubmitMessage}}
            </p>
          </ConfirmationDialog>

          <ConfirmationDialog
            @isOpen={{this.showConfirmApproverOverridePrompt}}
            @title="Out of Sequence Approver"
            @intent="warning"
            @onConfirm={{this.submitTask.perform}}
            @onCancel={{fn (mut this.showConfirmApproverOverridePrompt) false}}
            @confirmText="Yes"
            @cancelText="No"
          >
            <p class="mb-2 font-semibold">
              {{t "tuition_assistance.application_details.out_of_sequence_approver_warning"}}
            </p>
          </ConfirmationDialog>
        {{/if}}
        {{#if (and this.coursesInAttendAndNotReadonly @application.activeCourses.length)}}
          <div class="my-8 flex flex-col justify-center content-center gap-2">
            <Button
              @intent="primary"
              {{on "click" this.submitForEvidenceApprovalOnEmployeeBehalf.perform}}
              disabled={{false}}
              class="self-center px-8 w-fit"
            >
              {{t "tuition_assistance.application_details.submit_on_behalf_of_employee"}}
            </Button>
            {{#if this.submitOnBehalfMissingRecs.length}}
              <ErrorMessages
                @showErrors={{true}}
                @error={{this.missingRecsAlert}}
                class="tio-row-tight text-sm"
              />
            {{/if}}
          </div>
        {{/if}}
      </VStack>
      {{! TODO: as this is optional the CSS should take into account doing full when this is not present }}
      {{#unless Summary.isUnlimitedRemainingBenefit}}
        <Summary.Container
          @title={{t "tuition_assistance.application_details.assistance_summary"}}
          @titleClass="leading-6"
          class="w-full lg:!w-1/3 md:!w-1/2"
        >
          <Summary.MaxAnnualBenefitLineItem />
          {{#unless @application.shouldDisplayPaymentDetails}}
            <Summary.RemainingBenefitLineItem />
          {{/unless}}
        </Summary.Container>
      {{/unless}}
    </LimitsSummary>
    <Modal
      @isOpen={{this.showRejectModal}}
      @onClose={{this.setShowRejectModal}}
      class="min-h-64"
      @ariaLabel={{t "common.aria_labels.reject_application"}}
    >
      <:header>{{t "tuition_assistance.application_details.reject_application"}}</:header>
      <:body>
        {{#if @application.canRequestRepayment}}
          <div class="flex flex-row">
            <p class="text-xl my-2 text-tio-gray-800">
              {{t "tuition_assistance.application_details.repayment_amount"}}
            </p>

            <p class="text-xl my-4 text-tio-gray-800 ml-auto">
              {{safeFormatNumber
                (divide this.requestedRepaymentAmount 100)
                style="currency"
                currency="USD"
              }}</p>
          </div>
          {{#if @application.isPrepaidProgram}}
            <AlertBanner @intent="info">
              <p>
                {{t "tuition_assistance.application_details.rejecting_prepaid_initiates"}}
              </p>
            </AlertBanner>
          {{/if}}
        {{/if}}
        <hr class="my-4" />
        <Textarea
          @value={{this.rejectNote}}
          @onChange={{this.updateRejectNote}}
          placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
        />
        {{#if this.rejectError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.rejectError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.isRejectionTaskRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRejection}}
          @onCancel={{this.setShowRejectModal}}
        />
      </:footer>
    </Modal>
  </template>
}
